/**
 * CONTRACT.md §2.9 — tables.
 *
 * **The arithmetic build-up is a table.** §5 refused `.ladder`: the components
 * of `$5,510 delivered − $4,350 paid = $1,160 kept` are rows, `Tr variant="sub"`
 * indents a part and `Tr variant="total"` draws the ink rule and the sum. There
 * is no ladder here and there is not going to be one.
 *
 * `Tr variant="bad"` is for a genuinely failed line sitting in a ledger that is
 * mostly fine — §2.9: "Do **not** tint a table where every row failed: if
 * everything is flagged, nothing is flagged." That is a review rule.
 *
 * `Caption` is "optional and usually wrong" (§2.9). When the `THead` names the
 * columns and the region's `Answer` says what the table is, leave it out.
 */

import type { ReactNode } from 'react'

import { cx, type Common } from './common.js'

export type TableProps = Common & { readonly children: ReactNode }

export function Table({ children, ...common }: TableProps) {
  return (
    <table className="table" {...common}>
      {children}
    </table>
  )
}

export type TableSectionProps = Common & { readonly children: ReactNode }

export function THead({ children, ...common }: TableSectionProps) {
  return <thead {...common}>{children}</thead>
}

export function TBody({ children, ...common }: TableSectionProps) {
  return <tbody {...common}>{children}</tbody>
}

/** `total` draws the ink rule and the sum · `sub` indents a component ·
 *  `head` is a subhead row · `need` and `bad` tint one row. */
export type TrVariant = 'total' | 'sub' | 'head' | 'need' | 'bad'

const TR_CLASS: Record<TrVariant, string> = {
  total: 'is-total',
  sub: 'is-sub',
  head: 'is-head',
  need: 'is-need',
  bad: 'is-bad',
}

export type TrProps = Common & {
  readonly variant?: TrVariant | undefined
  /**
   * `.is-saved` — this row was just written to, for about a third of a second.
   *
   * A boolean rather than a sixth `variant`, because it is not one: a variant says
   * what a row *is* and two of them cannot be true at once, while a save happens to a
   * row that is already late, or blocked, or neither. It is also the one thing on a
   * table that is about time rather than about state, which is why the animation is
   * in the stylesheet and the row only says that it happened.
   */
  readonly saved?: boolean | undefined
  readonly children: ReactNode
}

export function Tr({ variant, saved, children, ...common }: TrProps) {
  return (
    <tr
      className={cx(
        variant === undefined ? false : TR_CLASS[variant],
        saved === true && 'is-saved',
      )}
      {...common}
    >
      {children}
    </tr>
  )
}

export type ThProps = Common & {
  /** `.right` — a column of figures. */
  readonly right?: boolean | undefined
  readonly colSpan?: number | undefined
  readonly scope?: 'col' | 'row' | undefined
  readonly children?: ReactNode
}

export function Th({ right, colSpan, scope, children, ...common }: ThProps) {
  return (
    <th
      className={right === true ? 'right' : undefined}
      colSpan={colSpan}
      scope={scope ?? 'col'}
      {...common}
    >
      {children}
    </th>
  )
}

export type TdProps = Common & {
  readonly right?: boolean | undefined
  /** `.num` on the cell itself — mono, tabular, and nowrap by the `.table td.num` rule. */
  readonly num?: boolean | undefined
  /** `.ref` on the cell itself. */
  readonly ref?: boolean | undefined
  /** `.date` on the cell itself. */
  readonly date?: boolean | undefined
  /** `.muted`, for the cell that says "baseline" rather than a figure. */
  readonly muted?: boolean | undefined
  /** `.nowrap`, for a cell that is neither a number nor a ref and still must not break. */
  readonly nowrap?: boolean | undefined
  readonly colSpan?: number | undefined
  readonly children?: ReactNode
}

export function Td({
  right,
  num,
  ref: isRef,
  date,
  muted,
  nowrap,
  colSpan,
  children,
  ...common
}: TdProps) {
  return (
    <td
      className={cx(
        right && 'right',
        num && 'num',
        isRef && 'ref',
        date && 'date',
        muted && 'muted',
        nowrap && 'nowrap',
      )}
      colSpan={colSpan}
      {...common}
    >
      {children}
    </td>
  )
}

export type CaptionProps = Common & { readonly children: ReactNode }

export function Caption({ children, ...common }: CaptionProps) {
  return <caption {...common}>{children}</caption>
}
